const TYPE_MAP = {
  lipid: "Lipid Profile",
  blood: "Complete Blood Count",
  cbc: "Complete Blood Count",
  metabolic: "Metabolic Panel",
  thyroid: "Thyroid (TSH)",
  renal: "Kidney Function",
  kidney: "Kidney Function",
  glucose: "Glucose Panel",
  diabetes: "HbA1c / Glucose",
  liver: "Liver Function",
  general: "Medical Report"
};

const CATEGORY_MAP = {
  lipid: "Cardiology",
  blood: "Hematology",
  cbc: "Hematology",
  metabolic: "Laboratory",
  thyroid: "Endocrinology",
  renal: "Nephrology",
  kidney: "Nephrology",
  glucose: "Diabetes",
  diabetes: "Diabetes",
  liver: "Gastroenterology",
  general: "General"
};

function displayNameFromReport(report) {
  if (report.reportType && TYPE_MAP[report.reportType]) {
    return TYPE_MAP[report.reportType];
  }
  if (report.reportType) {
    return report.reportType
      .split(/[_\s]+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }
  const base = report.fileName?.replace(/\.pdf$/i, "") || "Medical Report";
  return base
    .split(/[-_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function categoryFromReport(report) {
  const t = report.reportType;
  if (t && CATEGORY_MAP[t]) return CATEGORY_MAP[t];
  return "General";
}

function formatRowDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatKeyResult(report) {
  const metrics = report.extractedData?.metrics;
  if (!metrics || typeof metrics !== "object") return "—";
  const values = Object.values(metrics).filter(Boolean);
  if (!values.length) return "—";
  return values
    .slice(0, 2)
    .map((m) => {
      const u = m.unit ? ` ${m.unit}` : "";
      return `${m.label}: ${m.value}${u}`;
    })
    .join(" · ");
}

function deriveStatus(report) {
  const metrics = report.extractedData?.metrics;
  if (!metrics || typeof metrics !== "object" || Object.keys(metrics).length === 0) {
    return "Pending";
  }
  const hba1c = metrics.hba1c?.value;
  if (typeof hba1c === "number" && hba1c >= 6.5) return "Attention";
  const vitD = metrics.vitaminD?.value;
  if (typeof vitD === "number" && vitD < 25) return "Critical";
  const ldl = metrics.ldl?.value;
  if (typeof ldl === "number" && ldl > 160) return "Attention";
  const trig = metrics.triglycerides?.value;
  if (typeof trig === "number" && trig > 200) return "Attention";
  return "Normal";
}

export function mapApiReportToMediTrackRow(report) {
  const pd = report.extractedData?.patientDetails || {};
  const metrics = report.extractedData?.metrics;
  return {
    id: report._id,
    rawDate: report.uploadedAt,
    date: formatRowDate(report.uploadedAt),
    fileName: report.fileName || "—",
    testName: displayNameFromReport(report),
    category: categoryFromReport(report),
    keyResult: formatKeyResult(report),
    status: deriveStatus(report),
    doctor: pd.referredBy?.trim() || "—",
    reportType: report.reportType || "general",
    metrics: metrics && typeof metrics === "object" ? metrics : {}
  };
}

export function mapProfileReportsToMediTrackRows(reports) {
  if (!Array.isArray(reports)) return [];
  return reports.map(mapApiReportToMediTrackRow).sort((a, b) => {
    const ta = a.rawDate ? new Date(a.rawDate).getTime() : 0;
    const tb = b.rawDate ? new Date(b.rawDate).getTime() : 0;
    return tb - ta;
  });
}

export function buildSummaryFromRows(rows) {
  const total = rows.length;
  const normal = rows.filter((r) => r.status === "Normal").length;
  const attention = rows.filter((r) => r.status === "Attention").length;
  const critical = rows.filter((r) => r.status === "Critical").length;

  let lastValue = "—";
  let lastNote = "Upload a report to get started";
  if (rows.length) {
    const latest = rows[0];
    lastValue = latest.date;
    lastNote = latest.testName;
  }

  return [
    { key: "total", label: "Total Reports", value: String(total), note: "All time records", icon: "clipboard" },
    { key: "normal", label: "Normal", value: String(normal), note: "Within reference range", icon: "check" },
    { key: "attention", label: "Needs attention", value: String(attention), note: "Slight deviations", icon: "alert" },
    { key: "critical", label: "Critical", value: String(critical), note: "Review soon", icon: "alertOctagon" },
    { key: "last", label: "Last report", value: lastValue, note: lastNote, icon: "calendar" }
  ];
}
